/**
 * API semântica de medição do funil de aquisição do teste grátis.
 *
 * Único ponto de entrada para o resto do app: páginas e componentes chamam
 * `trackTrialCtaClick` / `trackSignUp` / `trackGenerateLead` e nunca tocam em
 * `window.gtag`.
 *
 * Funil medido no GA4:
 *   page_view → trial_cta_click → generate_lead
 *
 * `trial_cta_click` = interesse. `generate_lead` = conta criada com sucesso.
 * Login NUNCA gera lead.
 *
 * Cada função filtra os parâmetros pela allowlist do evento antes de enviar:
 * nada fora do contrato — e nenhum dado pessoal — chega ao GA4.
 */

import {
  ATTRIBUTION,
  BUSINESS_SEGMENTS,
  BUSINESS_SEGMENT_VALUES,
  CTA_PAGES,
  CTA_PAGE_VALUES,
  CTA_PLACEMENT_VALUES,
  DESTINATION_VALUES,
  DIRECT_VALUE,
  EVENTS,
  LEAD_SOURCE,
  PLAN_TYPE_VALUES,
  PRODUCT_VALUES,
  SIGN_UP_METHOD,
} from "./constants";
import {
  clearCtaContext,
  readCtaContext,
  saveCtaContext,
} from "./attribution";
import { resolveContextFromPath } from "./maps";
import { sendEvent } from "./gtag";

/** Parâmetros aceitos por evento — nada além disso é enviado. */
const EVENT_PARAM_ALLOWLIST = Object.freeze({
  [EVENTS.TRIAL_CTA_CLICK]: Object.freeze([
    "cta_page",
    "cta_placement",
    "business_segment",
    "destination",
    "plan_type",
    "product",
  ]),
  [EVENTS.SIGN_UP]: Object.freeze(["method"]),
  [EVENTS.GENERATE_LEAD]: Object.freeze([
    "lead_source",
    "attribution",
    "cta_page",
    "cta_placement",
    "business_segment",
    "product",
  ]),
});

/**
 * Remove do payload tudo que não está na allowlist do evento e todo valor
 * vazio/indefinido.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} params
 * @returns {Record<string, string>}
 */
function filterParams(eventName, params) {
  const allowed = EVENT_PARAM_ALLOWLIST[eventName] || [];
  const output = {};

  allowed.forEach((key) => {
    const value = params[key];
    if (typeof value === "string" && value.length > 0) output[key] = value;
  });

  return output;
}

/**
 * Devolve `value` se pertencer ao enum; senão o fallback.
 * @param {unknown} value
 * @param {ReadonlyArray<string>} allowedValues
 * @param {string|undefined} fallback
 */
function pickEnum(value, allowedValues, fallback) {
  return allowedValues.includes(value) ? value : fallback;
}

/**
 * Clique em CTA de teste grátis: dispara `trial_cta_click` e grava o contexto
 * de atribuição para o cadastro que possa vir depois.
 *
 * Blindado em try/catch: os CTAs chamam esta função no `onClick`, e no
 * `<Link>` do react-router uma exceção aqui impediria a navegação interna
 * (`internalOnClick` só roda se o `onClick` não lançar); nos CTAs que são
 * `<button>` o clique morreria em silêncio. Medição nunca bloqueia o fluxo.
 *
 * @param {{cta_page: string, cta_placement: string, business_segment: string,
 *          destination: string, plan_type?: string, product?: string}} input
 */
export function trackTrialCtaClick(input = {}) {
  try {
    const context = {
      cta_page: pickEnum(input.cta_page, CTA_PAGE_VALUES, CTA_PAGES.UNKNOWN),
      cta_placement: pickEnum(
        input.cta_placement,
        CTA_PLACEMENT_VALUES,
        undefined,
      ),
      business_segment: pickEnum(
        input.business_segment,
        BUSINESS_SEGMENT_VALUES,
        BUSINESS_SEGMENTS.GENERAL,
      ),
      destination: pickEnum(input.destination, DESTINATION_VALUES, undefined),
    };

    const planType = pickEnum(input.plan_type, PLAN_TYPE_VALUES, undefined);
    if (planType) context.plan_type = planType;

    // `product` é opcional e sem fallback de propósito: um CTA que ainda não
    // sabe o produto (o da home, antes do modal) não deve inventar um. Ausente
    // no payload é informação — "o clique não escolheu" —, e um default aqui
    // apagaria justamente isso.
    const product = pickEnum(input.product, PRODUCT_VALUES, undefined);
    if (product) context.product = product;

    sendEvent(
      EVENTS.TRIAL_CTA_CLICK,
      filterParams(EVENTS.TRIAL_CTA_CLICK, context),
    );

    // Só faz sentido atribuir um lead futuro a um CTA identificado.
    if (context.cta_placement && context.destination) saveCtaContext(context);
  } catch (_err) {
    // medição nunca bloqueia o clique
  }
}

/** Cadastro concluído — método usado. */
export function trackSignUp() {
  sendEvent(
    EVENTS.SIGN_UP,
    filterParams(EVENTS.SIGN_UP, { method: SIGN_UP_METHOD.EMAIL_PASSWORD }),
  );
}

/**
 * Lead = conta criada com sucesso. Com contexto de CTA válido, atribui ao
 * clique; sem contexto, marca como entrada direta.
 *
 * Sem `value` e sem `currency` — o lead do teste grátis não tem preço.
 *
 * @param {object|null} [ctx] contexto vindo de `readCtaContext()`.
 */
export function trackGenerateLead(ctx) {
  const hasContext =
    ctx &&
    CTA_PAGE_VALUES.includes(ctx.cta_page) &&
    CTA_PLACEMENT_VALUES.includes(ctx.cta_placement) &&
    BUSINESS_SEGMENT_VALUES.includes(ctx.business_segment);

  const params = hasContext
    ? {
        lead_source: LEAD_SOURCE.FREE_TRIAL_SIGNUP,
        attribution: ATTRIBUTION.CTA,
        cta_page: ctx.cta_page,
        cta_placement: ctx.cta_placement,
        business_segment: ctx.business_segment,
        // Sem isto, o `trial_cta_click` saberia o produto e a CONVERSÃO não —
        // e a pergunta que motivou a dimensão ("qual produto converte melhor?")
        // ficaria sem resposta justamente no evento que a responde.
        // `filterParams` descarta se vier vazio/ausente.
        product: ctx.product,
      }
    : {
        lead_source: LEAD_SOURCE.FREE_TRIAL_SIGNUP,
        attribution: ATTRIBUTION.DIRECT,
        cta_page: CTA_PAGES.DIRECT,
        cta_placement: DIRECT_VALUE,
        business_segment: BUSINESS_SEGMENTS.GENERAL,
      };

  sendEvent(EVENTS.GENERATE_LEAD, filterParams(EVENTS.GENERATE_LEAD, params));
}

export { clearCtaContext, readCtaContext, resolveContextFromPath };

// Enums re-exportados para que páginas e componentes nunca escrevam literais.
export {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  PLAN_TYPE,
} from "./constants";
